import {Component, OnDestroy, OnInit} from '@angular/core';
import {formatDate, formatNumber} from '@angular/common';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {
  IPlatformToolComponent,
  IPlatformToolHandler
} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommandResponse} from '@shared/commands/command-base';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {hasFeature} from '@shared/store/selectors/security.selector';
import {SnackBarService} from '@core/services/snack-bar.service';
import {Configuration} from '@shared/models/configuration';
import {AdminDashboardReportPdfService} from '@features/admin-panel/services/admin-dashboard-report-pdf.service';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {defaultAdminDashboardScope} from '@features/admin-panel/helpers/admin-dashboard-default-scope';
import {
  AdminDashboardFilters,
  AdminDashboardKpi,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardReportHeader} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardDataStatus} from '@features/admin-panel/models/admin-dashboard-frame.models';

/** One KPI tile on the dashboard: the query that fills it and the FeatureId that gates it. */
interface AdminDashboardTileDef {
  query: string;
  feature: string;
}

/** One section card: its id in `widgetStates`, the FeatureId that gates it, and whether only the Clients scope shows it. */
interface AdminDashboardWidgetDef {
  id: string;
  feature: string;
  clientsOnly?: boolean;
}

/** The period as the PDF report names it. */
const REPORT_PERIOD_LABELS: {[period: string]: string} = {
  '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', '12m': 'Last 12 months'
};

/** The header filters as the PDF report names them, in the filter bar's order. */
const REPORT_FILTER_LABELS: {field: keyof AdminDashboardFilters; label: string}[] = [
  {field: 'packages', label: 'Package'},
  {field: 'sources', label: 'Source'},
  {field: 'licenceStatuses', label: 'Licence status'},
  {field: 'owners', label: 'Account owner'}
];

/**
 * The Admin Panel's landing ('/' → AdminDashboardToolLinkCommand). A header bar with the Clients / Non-clients
 * switch, the period picker and the filters above a KPI strip whose tiles are each gated by their own FeatureId; a tile whose feature is not granted,
 * whose query fails, or that can only say "No Client Manager record" (not applicable in the scope, ITP-E-173-04), is
 * not shown. Below it, the "Licences & revenue" section's widgets, each gated by its own
 * FeatureId too. Picking a period or changing a filter reloads every tile and widget for it; switching the scope
 * also clears the filters. Every open starts on Non-clients on the dev, QA and stage clusters, whose Client Manager
 * holds no real clients, and on Clients elsewhere — the scope is not persisted. A section card with nothing to show
 * is hidden, its row-mate fills the row, and a section whose cards are all hidden goes with its heading (ITP-E-173).
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss']
})
export class AdminDashboardComponent implements OnInit, OnDestroy, IPlatformToolComponent {

  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  scope: AdminDashboardScope = defaultAdminDashboardScope(Configuration.Environment);
  period = '30d';
  filters: AdminDashboardFilters = {};
  /** The companies in the scope before any filter, from the filter bar's options (ITP-E-162-06); null until they load. */
  scopeTotal: number | null = null;
  readonly tiles: AdminDashboardTileDef[] = [
    {query: 'AdminDashboardActiveCompaniesQuery', feature: 'APManagement'},
    {query: 'AdminDashboardActiveUsersQuery', feature: 'APManagement'},
    {query: 'AdminDashboardArrQuery', feature: 'APClientManager'},
    {query: 'AdminDashboardRenewalsDueQuery', feature: 'APClientManager'},
    {query: 'AdminDashboardRevenueAtRiskQuery', feature: 'APClientManager'},
    {query: 'AdminDashboardAccountsAtRiskQuery', feature: 'APManagement'},
    {query: 'AdminDashboardTrialConversionQuery', feature: 'APBilling'},
    {query: 'AdminDashboardXatBotSpendQuery', feature: 'APUsageReport'} // GEN-E-182-23: the Usage Report app's feature
  ];
  kpis: AdminDashboardKpi[] = [];
  /** The params of the last load; the section widgets load themselves whenever it changes. */
  params: AdminDashboardQueryParams | null = null;
  selectedCompanyId: string | null = null;
  exportingPdf = false;
  /**
   * The nightly run's date from the attention band (its `asOf` output). Since ITP-E-162-02 it is only the fallback of the
   * page bar's as-of date, used when the data-status query has not answered.
   */
  dataThrough: string | null = null;
  /** The page bar's freshness fact from AdminDashboardDataStatusQuery (ITP-E-162-02); null until it answers or when it fails. */
  dataStatus: AdminDashboardDataStatus | null = null;
  /** Each section card's last reported state, by its id (ITP-E-173-01). */
  widgetStates: {[widgetId: string]: AdminDashboardWidgetState} = {};

  private loadSubscription: Subscription;
  private dataStatusSubscription: Subscription;
  private granted: (feature: string) => boolean = () => false;

  constructor(
    private dataService: AdminDashboardDataService,
    private store: Store<SecurityState>,
    private reportPdfService: AdminDashboardReportPdfService,
    private reportRegistry: AdminDashboardReportRegistry,
    private snackBarService: SnackBarService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadDataStatus();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.dataStatusSubscription?.unsubscribe();
  }

  onLoad(): void {
    this.load();
  }

  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse): void {}
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse): void {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }

  /**
   * "887 client companies" or "12 companies not in Client Manager": the Active companies KPI's total (the companies
   * in scope after the filters, AdminDashboardActiveCompaniesQueryHandler) at the end of the header; null without
   * that KPI (not granted, failed or not loaded yet). It replaces the old title's scope subtitle (ITP-E-148-06).
   * While a filter is on and the scope's unfiltered total is known it says "212 of 887 client companies" /
   * "12 of 40 companies not in Client Manager" (ITP-E-162-06, Q7).
   */
  get companyCountText(): string | null {
    const total = this.kpis.find(kpi => kpi.key === 'active-companies')?.total;
    if (total === null || total === undefined) {
      return null;
    }
    const nonClients = this.scope === 'NonClients';
    if (this.hasActiveFilters && this.scopeTotal !== null) {
      const matching = formatNumber(total, 'en-US', '1.0-0');
      return nonClients
        ? `${matching} of ${countOf(this.scopeTotal, 'company', 'companies')} not in Client Manager`
        : `${matching} of ${countOf(this.scopeTotal, 'client company', 'client companies')}`;
    }
    return nonClients
      ? `${countOf(total, 'company', 'companies')} not in Client Manager`
      : countOf(total, 'client company', 'client companies');
  }

  /** True while any header filter has a value (an empty field means no filter). */
  get hasActiveFilters(): boolean {
    return (Object.keys(this.filters) as (keyof AdminDashboardFilters)[]).some(field => !!this.filters[field]?.length);
  }

  onScopeChange(scope: AdminDashboardScope): void {
    this.scope = scope;
    this.filters = {};
    this.scopeTotal = null;
    this.load();
  }

  /** The filter bar loaded its options: the new scope's unfiltered company total (ITP-E-162-06). */
  onScopeTotal(total: number): void {
    this.scopeTotal = total;
  }

  /** A company was picked — a Needs attention row, an activity-ranking row or a seat-utilisation dot: the why panel opens for it. */
  onCompanySelected(companyId: string): void {
    this.selectedCompanyId = companyId;
  }

  /** A KPI tile was clicked (ITP-E-149-04): its section scrolls into view. A section hidden by its feature gate is skipped. */
  onKpiSection(sectionId: string): void {
    document.getElementById(sectionId)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  /**
   * The page bar fact (ITP-E-162-02, OCA-E-159 page.tsx:128-131): "Data through 2 Oct · refreshed 3 Oct 2026, 06:00 UTC"
   * once the data-status query knows the refresh minute, else "Data through 2 Oct 2026"; null (the fact is hidden) while
   * no as-of date is known. The as-of date is the data status's, else the attention band's (the same nightly day).
   */
  get dataThroughText(): string | null {
    const asOf = this.dataStatus?.asOf ?? this.dataThrough;
    if (!asOf) {
      return null;
    }
    const refreshedAt = this.dataStatus?.refreshedAt;
    return refreshedAt
      ? `Data through ${formatDate(asOf, 'd MMM', 'en-US', 'UTC')} · refreshed ${formatDate(refreshedAt, 'd MMM yyyy, HH:mm', 'en-US', 'UTC')} UTC`
      : `Data through ${formatDate(asOf, 'd MMM yyyy', 'en-US', 'UTC')}`;
  }

  /**
   * The attention band's data arrived: its nightly as-of date, or null before the first run (ITP-E-148-07). Applied a
   * microtask later for the same reason as `onWidgetState`: the page bar showing it is bound before the band reports.
   */
  onAsOf(asOf: string | null): void {
    Promise.resolve().then(() => this.dataThrough = asOf);
  }

  /**
   * A section card reported what it has to show. Applied a microtask later: a card reports during the change
   * detection that loads it, after this page has bound the spans and hidden classes that depend on it, and changing
   * them in the same pass is an ExpressionChangedAfterItHasBeenChecked error.
   */
  onWidgetState(widgetId: string, state: AdminDashboardWidgetState): void {
    Promise.resolve().then(() => this.widgetStates[widgetId] = state);
  }

  /**
   * A card with nothing to show is hidden by a host class, not removed: it stays mounted, so it reloads with the
   * params and comes back when it has something to show. A card still loading or one that failed is never hidden.
   */
  isHidden(widgetId: string): boolean {
    return this.widgetStates[widgetId] === 'empty';
  }

  /** A flag was snoozed or handled: a fresh params object makes the attention band reload via `ngOnChanges`. */
  onWhyChanged(): void {
    this.params = this.params ? {...this.params} : this.params;
  }

  onPeriodChange(period: string): void {
    this.period = period;
    this.load();
  }

  onFiltersChange(filters: AdminDashboardFilters): void {
    this.filters = filters;
    this.load();
  }

  /** The gear in the header: the Prediction settings page (ITP-E-142-07). */
  openSettings(): void {
    this.router.navigateByUrl('/admin-panel/dashboard-settings');
  }

  /** Saves the dashboard as a designed A4 report: the KPIs and every card's report blocks (ITP-E-146-06). */
  exportPdf(): void {
    if (this.exportingPdf) {
      return;
    }
    this.exportingPdf = true;
    this.reportPdfService.export(this.reportHeader(new Date()), this.kpis, this.reportRegistry.collect()).then(
      () => {
        this.exportingPdf = false;
      },
      () => {
        this.exportingPdf = false;
        this.snackBarService.showError('Could not export the dashboard. Try again.');
      });
  }

  /** The PDF report's header: the scope, the period and the filters as the dashboard shows them, and when it was made. */
  reportHeader(generatedAt: Date): AdminDashboardReportHeader {
    const filters = REPORT_FILTER_LABELS
      .filter(({field}) => this.filters[field]?.length)
      .map(({field, label}) => `${label}: ${this.filters[field].join(', ')}`);
    return {
      scopeLabel: this.scope === 'NonClients' ? 'Non-clients' : 'Clients',
      periodLabel: REPORT_PERIOD_LABELS[this.period] ?? this.period,
      filtersLabel: filters.length ? `Filters: ${filters.join('; ')}` : 'No filters',
      generatedAt
    };
  }

  load(): void {
    this.loadSubscription?.unsubscribe();
    // No take(1): hasFeature emits a function before the user's features have loaded (the security state starts
    // with features = {}), so every later emission re-grants and reloads. The subscription ends on the next load
    // or on destroy.
    this.loadSubscription = this.store.pipe(
      select(hasFeature),
      filter(fn => !!fn),
      switchMap(fn => {
        this.granted = feature => fn(feature) === true;
        this.params = this.queryParams();
        return this.loadGrantedKpis(this.tiles.filter(tile => this.granted(tile.feature)));
      })
    ).subscribe(kpis => this.kpis = kpis.filter(kpi => !!kpi && !kpi.notApplicable));
  }

  /** True when the FeatureId is granted by the latest features in the store. */
  isGranted(feature: string): boolean {
    return this.granted(feature);
  }

  /**
   * The section grid's span class for a card (ITP-E-151-01): `adb-span-<width>` of 12 columns while the card that
   * shares its row is granted and not hidden (ITP-E-173-01), else `adb-span-12` — a card whose row-mate is gated off
   * or has nothing to show fills the row instead of leaving a hole beside it.
   */
  spanClass(width: number, rowMateFeature: string, rowMateId: string): string {
    return this.isGranted(rowMateFeature) && !this.isHidden(rowMateId) ? `adb-span-${width}` : 'adb-span-12';
  }

  /** The Revenue coverage card (ITP-E-160-09): the Clients scope only, gated by APClientManager. */
  get showRevenueCoverage(): boolean {
    return this.scope === 'Clients' && this.isGranted('APClientManager');
  }

  /**
   * The span of each card in the "Licences & revenue" section's second row (ITP-E-160-09): Billing (APBilling), API
   * keys (APManagement) and Revenue coverage share the 12 columns equally — 4 / 4 / 4, 6 / 6 when one is not shown,
   * 12 alone — so a card that is gated off or has nothing to show (ITP-E-173-01) never leaves a hole (the ITP-E-151-01
   * rule for a row of three).
   */
  get licencesRowTwoSpan(): string {
    const shown = [
      this.isGranted('APBilling') && !this.isHidden('billing-this-month'),
      this.isGranted('APManagement') && !this.isHidden('api-keys-expiring'),
      this.showRevenueCoverage && !this.isHidden('revenue-coverage')
    ].filter(Boolean).length;
    return shown >= 3 ? 'adb-span-4' : shown === 2 ? 'adb-span-6' : 'adb-span-12';
  }

  /**
   * A section's cards are all hidden: the section goes with its heading, but stays mounted so its cards reload. Only
   * the cards on screen count: granted, and in the current scope when the card is Clients-only.
   */
  isSectionHidden(widgets: AdminDashboardWidgetDef[]): boolean {
    return widgets.filter(widget => this.isGranted(widget.feature) && (!widget.clientsOnly || this.scope === 'Clients'))
      .every(widget => this.isHidden(widget.id));
  }

  /** The "Licences & revenue" section's widgets. */
  readonly licencesAndRevenueWidgets: AdminDashboardWidgetDef[] = [
    {id: 'expiry-timeline', feature: 'APClientManager'},
    {id: 'trials-ending', feature: 'APBilling'},
    {id: 'billing-this-month', feature: 'APBilling'},
    {id: 'api-keys-expiring', feature: 'APManagement'},
    {id: 'revenue-coverage', feature: 'APClientManager', clientsOnly: true}
  ];

  /** The "Licences & revenue" section shows once any of its widgets is granted. */
  get showLicencesAndRevenue(): boolean {
    return !!this.params && this.licencesAndRevenueWidgets.some(widget => this.isGranted(widget.feature));
  }

  /** The "Deeper analysis" section's cards. */
  readonly deeperAnalysisWidgets: AdminDashboardWidgetDef[] = [
    {id: 'cohort-retention', feature: 'APManagement'},
    {id: 'trial-funnel', feature: 'APBilling'}
  ];

  /** The "Deeper analysis" section shows once any of its cards is granted. */
  get showDeeperAnalysis(): boolean {
    return !!this.params && this.deeperAnalysisWidgets.some(widget => this.isGranted(widget.feature));
  }

  /** The "Users & adoption" section's cards. */
  readonly usersAdoptionWidgets: AdminDashboardWidgetDef[] = [
    {id: 'logins-per-day', feature: 'APManagement'},
    {id: 'signups-per-week', feature: 'APManagement'},
    {id: 'activity-ranking', feature: 'APManagement'},
    {id: 'seat-utilisation', feature: 'APClientManager'}
  ];

  /** The "Users & adoption" section shows once any of its cards is granted. */
  get showUsersAdoption(): boolean {
    return !!this.params && this.usersAdoptionWidgets.some(widget => this.isGranted(widget.feature));
  }

  /**
   * The "Projects, scenarios & entities" section's cards (ITP-E-214, Plan ITP-P-50 SPEC WEB-3). Each card ticket adds
   * its def on its own line, ending with a comma, directly above the closing `];`.
   */
  readonly platformUsageWidgets: AdminDashboardWidgetDef[] = [
    {id: 'projects-trend', feature: 'APManagement'},
    {id: 'scenarios-trend', feature: 'APManagement'},
    {id: 'entities-trend', feature: 'APManagement'},
    {id: 'usage-activity', feature: 'APManagement'},
    {id: 'entity-headroom', feature: 'APClientManager', clientsOnly: true},
    {id: 'activation-funnel', feature: 'APManagement'},
    {id: 'seasonal-load', feature: 'APManagement'},
  ];

  /**
   * The "Projects, scenarios & entities" section shows with APManagement, its own gate (Plan ITP-P-50 Q8), or once any
   * of its cards is granted; while none of its cards has anything to show, isSectionHidden hides it, heading included.
   */
  get showPlatformUsage(): boolean {
    return !!this.params && (this.isGranted('APManagement') || this.platformUsageWidgets.some(widget => this.isGranted(widget.feature)));
  }

  /**
   * The Activation funnel's span (ITP-E-217-03): 6 beside the Entity licence headroom card (APClientManager, Clients
   * only, ITP-E-215) while that card is granted and not hidden, else the whole row. On Non-clients the headroom card
   * answers NotApplicable and is hidden, so the funnel always fills the row there, even while the headroom card's last
   * reported state is still a stale 'content' from the Clients scope.
   */
  get activationFunnelSpan(): string {
    return this.scope === 'Clients' ? this.spanClass(6, 'APClientManager', 'entity-headroom') : 'adb-span-12';
  }

  /** The "Product quality" section's cards. GEN-E-182-25: the XatBot cards follow the Usage Report app's feature. */
  readonly productQualityWidgets: AdminDashboardWidgetDef[] = [
    {id: 'xatbot-quality', feature: 'APUsageReport'},
    {id: 'failing-topics', feature: 'APUsageReport'},
    {id: 'init-failures', feature: 'APManagement'}
  ];

  /** The "Product quality" section shows once any of its cards is granted. */
  get showProductQuality(): boolean {
    return !!this.params && this.productQualityWidgets.some(widget => this.isGranted(widget.feature));
  }

  private loadGrantedKpis(tiles: AdminDashboardTileDef[]): Observable<AdminDashboardKpi[]> {
    if (!tiles.length) {
      return of([]);
    }
    return forkJoin(tiles.map(tile =>
      this.dataService.getKpi(tile.query, this.queryParams()).pipe(catchError(() => of(null)))
    ));
  }

  /**
   * One AdminDashboardDataStatusQuery on open (ITP-E-162-02). It is gated by the landing feature "AP", so it runs before
   * and regardless of any card's feature; a failure leaves the attention band's date as the fallback.
   */
  private loadDataStatus(): void {
    this.dataStatusSubscription?.unsubscribe();
    this.dataStatusSubscription = this.dataService
      .getData<AdminDashboardDataStatus>('AdminDashboardDataStatusQuery', {scope: this.scope, period: this.period}, 'DataStatus')
      .pipe(catchError(() => of(null)))
      .subscribe(status => this.dataStatus = status ?? null);
  }

  /** Scope, period and every non-empty filter; an empty filter is left out so it means "no filter". */
  private queryParams(): AdminDashboardQueryParams {
    const params: AdminDashboardQueryParams = {scope: this.scope, period: this.period};
    (Object.keys(this.filters) as (keyof AdminDashboardFilters)[])
      .filter(field => this.filters[field]?.length)
      .forEach(field => params[field] = [...this.filters[field]]);
    return params;
  }
}
